import { CommonModule } from "@angular/common";
import { InjectionToken, ModuleWithProviders, NgModule } from "@angular/core";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonModule } from "@angular/material/button";
import { MatSidenavModule } from "@angular/material/sidenav";
import { RouterModule } from "@angular/router";
import { FoundationFooterModule } from "../footer/footer.module";
import { FoundationHeaderModule } from "../header/header.module";
import { RecursivePartial } from "../models/RecursivePartial";
import { ILayoutOptions } from "./ILayoutOptions";
import { FoundationLayoutComponent } from "./layout.component";
import { FoundationLayoutService } from "./layout.service";

/**
 * An intermediary injectable used to provide the raw config argument's value to the factory function
 * without causing AOT failures. NOTE: This value has to be exported otherwise the AoT compiler won't see it.
 */
export const LAYOUT_USER_OPTIONS = new InjectionToken<ILayoutOptions>("LAYOUT_OPTIONS");

/**
 * A simple factory that will get it's options value from the above token and create an options object.
 * "In order to play nicely with the AoT compiler, you have to extract the factory function declaration out
 * as a top-level exported value on the JavaScript module."
 * @see https://www.bennadel.com/blog/3565-providing-module-configuration-using-forroot-and-ahead-of-time-compiling-in-angular-7-2-0.htm
 */
export function LayoutModuleOptionsFactory(options: RecursivePartial<ILayoutOptions> = {}): RecursivePartial<ILayoutOptions> {
	return options;
}

@NgModule({
	declarations: [FoundationLayoutComponent],
	imports: [CommonModule, RouterModule, MatSidenavModule, FoundationHeaderModule, FoundationFooterModule, MatIconModule, MatButtonModule],
	providers: [FoundationLayoutService],
	exports: [FoundationLayoutComponent],
})
export class FoundationLayoutModule {
	/** Provide a configuration object for the Error Handler including required services that will be provided via `useExisting`. */
	static forRoot(config?: RecursivePartial<ILayoutOptions>): ModuleWithProviders<FoundationLayoutModule> {
		return {
			ngModule: FoundationLayoutModule,
			providers: [
				// Create and provide the service configuration options.
				{ provide: LAYOUT_USER_OPTIONS, useValue: config },
				{
					deps: [LAYOUT_USER_OPTIONS],
					provide: "LAYOUT_OPTIONS",
					useFactory: LayoutModuleOptionsFactory,
				},
				// Provide service
				FoundationLayoutService,
				// { provide: 'LAYOUT_SERVICE', useClass: FoundationLayoutService } We probably don't need the additional token here.
			],
		};
	}
}
